"use client";

import * as React from "react";
import { CustomModal } from "@/components/ui/CustomModal";
import {
  TABLE_DEFAULT_COLS,
  TABLE_DEFAULT_ROWS,
  TABLE_MAX_COLS,
  TABLE_MAX_ROWS,
  TABLE_SCHEMA_VERSION,
  type TableBlockData,
  type TableInitOptions,
} from "@/cms/blocks/table";
import { exportTableToXlsx, importXlsxFileToTable } from "./table-editor/excel";
import {
  areNumberArraysEqual,
  clampSize,
  cloneTable,
  createCell,
  getColumnCount,
  getSelectedAnchor,
  normalizeFixedIndex,
  normalizeSelection,
  resizeColumnWidths,
  safeSanitize,
  stringify,
  tableFromDoc,
  tableToDoc,
} from "./table-editor/model";
import { TableEditorToolbar } from "./table-editor/TableEditorToolbar";
import { TableGrid } from "./table-editor/TableGrid";
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_ROW_HEIGHT,
  type CellAlign,
  type CellDirection,
  type Coord,
  type ResizingState,
  type SelectionState,
  type TableCellModel,
} from "./table-editor/types";

type TableEditorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: TableBlockData;
  onChange: (updater: (prev: TableBlockData) => TableBlockData) => void;
};

type TableInsertModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (options: Required<TableInitOptions>) => void;
};

export function TableInsertModal({
  open,
  onClose,
  onSubmit,
}: TableInsertModalProps) {
  const [rows, setRows] = React.useState(TABLE_DEFAULT_ROWS);
  const [cols, setCols] = React.useState(TABLE_DEFAULT_COLS);
  const [headerRow, setHeaderRow] = React.useState(true);
  const [headerColumn, setHeaderColumn] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setRows(TABLE_DEFAULT_ROWS);
      setCols(TABLE_DEFAULT_COLS);
      setHeaderRow(true);
      setHeaderColumn(false);
    }
  }, [open]);

  const handleSubmit = React.useCallback(() => {
    onSubmit({
      rows: clampSize(rows, TABLE_MAX_ROWS),
      cols: clampSize(cols, TABLE_MAX_COLS),
      headerRow,
      headerColumn,
    });
    onClose();
  }, [cols, rows, headerColumn, headerRow, onClose, onSubmit]);

  return (
    <CustomModal
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
      maxWidth="max-w-md"
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Insert table</h2>
          <p className="text-sm text-gray-600">
            Choose starting dimensions (max {TABLE_MAX_ROWS} rows x{" "}
            {TABLE_MAX_COLS} columns).
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1 text-sm text-gray-700">
            <span className="block font-medium">Rows</span>
            <input
              type="number"
              min={1}
              max={TABLE_MAX_ROWS}
              value={rows}
              onChange={(event) => setRows(Number(event.target.value))}
              className="w-full rounded border border-gray-300 px-3 py-2"
            />
          </label>
          <label className="space-y-1 text-sm text-gray-700">
            <span className="block font-medium">Columns</span>
            <input
              type="number"
              min={1}
              max={TABLE_MAX_COLS}
              value={cols}
              onChange={(event) => setCols(Number(event.target.value))}
              className="w-full rounded border border-gray-300 px-3 py-2"
            />
          </label>
        </div>

        <div className="space-y-2 text-sm text-gray-700">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={headerRow}
              onChange={(event) => setHeaderRow(event.target.checked)}
              className="h-4 w-4"
            />
            <span>Header row</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={headerColumn}
              onChange={(event) => setHeaderColumn(event.target.checked)}
              className="h-4 w-4"
            />
            <span>Header column</span>
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded bg-pinkCustom px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
            onClick={handleSubmit}
          >
            Insert
          </button>
        </div>
      </div>
    </CustomModal>
  );
}

export function TableEditorModal({
  open,
  onOpenChange,
  data,
  onChange,
}: TableEditorModalProps) {
  const initial = React.useMemo(() => tableFromDoc(data?.doc), [data?.doc]);
  const [table, setTable] = React.useState<TableCellModel[][]>(
    () => initial.table,
  );
  const [columnWidths, setColumnWidths] = React.useState<number[]>(
    () => initial.columnWidths,
  );
  const [selection, setSelection] = React.useState<SelectionState>({
    start: { row: 0, col: 0 },
    end: { row: 0, col: 0 },
  });
  const [editingCell, setEditingCell] = React.useState<Coord | null>(null);
  const [hoveredResizeCol, setHoveredResizeCol] = React.useState<number | null>(
    null,
  );
  const [resizing, setResizing] = React.useState<ResizingState>(null);
  const rowRefs = React.useRef<Array<HTMLTableRowElement | null>>([]);
  const [rowHeights, setRowHeights] = React.useState<number[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [clamped, setClamped] = React.useState(initial.clamped);
  const [stats, setStats] = React.useState<{
    rows: number;
    cols: number;
    headerRows: number;
    headerCols: number;
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const onChangeRef = React.useRef(onChange);
  const lastPushedDocStrRef = React.useRef("");

  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    const next = tableFromDoc(data?.doc);
    const incomingDocStr = stringify(safeSanitize(data?.doc).doc);
    if (incomingDocStr === lastPushedDocStrRef.current) return;
    setTable(next.table);
    setColumnWidths(next.columnWidths);
    setSelection({ start: { row: 0, col: 0 }, end: { row: 0, col: 0 } });
    setEditingCell(null);
    setClamped(next.clamped);
    lastPushedDocStrRef.current = incomingDocStr;
  }, [data?.doc]);

  React.useEffect(() => {
    const nextDoc = tableToDoc(table, columnWidths);
    try {
      const { doc, stats: nextStats, clamped: isClamped } =
        safeSanitize(nextDoc);
      const docStr = stringify(doc);
      setStats(nextStats);
      setClamped(isClamped);
      setError(null);
      if (docStr === lastPushedDocStrRef.current) return;
      lastPushedDocStrRef.current = docStr;
      onChangeRef.current((prev) => ({
        ...prev,
        schemaVersion: TABLE_SCHEMA_VERSION,
        doc,
        fixedRow: normalizeFixedIndex(prev?.fixedRow, nextStats.rows),
        fixedCol: normalizeFixedIndex(prev?.fixedCol, nextStats.cols),
      }));
    } catch (err: any) {
      setError(err?.message ?? "Unable to validate this table.");
    }
  }, [table, columnWidths]);

  const selectedRange = normalizeSelection(selection);
  const hasSelection = Boolean(selectedRange);
  const selectedAnchor = getSelectedAnchor(selection);
  const selectedCell = table[selectedAnchor.row]?.[selectedAnchor.col];
  const fixedRow = normalizeFixedIndex(data?.fixedRow, table.length);
  const fixedCol = normalizeFixedIndex(data?.fixedCol, getColumnCount(table));

  const measureRowHeights = React.useCallback(() => {
    const nextHeights = table.map(
      (_, rowIndex) =>
        rowRefs.current[rowIndex]?.offsetHeight ?? DEFAULT_ROW_HEIGHT,
    );
    setRowHeights((current) =>
      areNumberArraysEqual(current, nextHeights) ? current : nextHeights,
    );
  }, [table]);

  React.useLayoutEffect(() => {
    measureRowHeights();
  }, [columnWidths, editingCell, measureRowHeights, table]);

  React.useEffect(() => {
    window.addEventListener("resize", measureRowHeights);
    return () => window.removeEventListener("resize", measureRowHeights);
  }, [measureRowHeights]);

  const updateSelectedCells = React.useCallback(
    (updater: (cell: TableCellModel) => TableCellModel) => {
      if (!selectedRange) return;
      setTable((current) => {
        const next = cloneTable(current);
        for (let row = selectedRange.fromRow; row <= selectedRange.toRow; row += 1) {
          for (let col = selectedRange.fromCol; col <= selectedRange.toCol; col += 1) {
            const cell = next[row]?.[col];
            if (!cell || cell.hidden) continue;
            next[row][col] = updater(cell);
          }
        }
        return next;
      });
    },
    [selectedRange],
  );

  const setSelectedAlign = React.useCallback(
    (align: CellAlign) => {
      updateSelectedCells((cell) => ({ ...cell, align }));
    },
    [updateSelectedCells],
  );

  const setSelectedDirection = React.useCallback(
    (direction: CellDirection) => {
      updateSelectedCells((cell) => ({ ...cell, direction }));
    },
    [updateSelectedCells],
  );

  const toggleSelectedBold = React.useCallback(() => {
    const nextBold = !selectedCell?.bold;
    updateSelectedCells((cell) => ({ ...cell, bold: nextBold }));
  }, [selectedCell?.bold, updateSelectedCells]);

  const toggleSelectedItalic = React.useCallback(() => {
    const nextItalic = !selectedCell?.italic;
    updateSelectedCells((cell) => ({ ...cell, italic: nextItalic }));
  }, [selectedCell?.italic, updateSelectedCells]);

  const selectCell = React.useCallback(
    (row: number, col: number, append = false) => {
      const cell = table[row]?.[col];
      if (!cell || cell.hidden) return;
      setEditingCell(null);
      setSelection((current) => ({
        start: append && current.start ? current.start : { row, col },
        end: { row, col },
      }));
    },
    [table],
  );

  const updateCellText = React.useCallback((value: string) => {
    setTable((current) => {
      const anchor = getSelectedAnchor(selection);
      const next = cloneTable(current);
      const cell = next[anchor.row]?.[anchor.col];
      if (cell && !cell.hidden) cell.text = value;
      return next;
    });
  }, [selection]);

  const addColumn = React.useCallback(
    (side: "before" | "after") => {
      const anchor = getSelectedAnchor(selection);
      const insertAt = side === "before" ? anchor.col : anchor.col + 1;
      setTable((current) =>
        current.map((row, rowIndex) => {
          const nextRow = [...row];
          const type = rowIndex === 0 ? "header" : "cell";
          nextRow.splice(insertAt, 0, createCell(rowIndex, insertAt, type));
          return nextRow.slice(0, TABLE_MAX_COLS);
        }),
      );
      setColumnWidths((current) => {
        const next = [...current];
        next.splice(insertAt, 0, current[insertAt - 1] ?? DEFAULT_COLUMN_WIDTH);
        return next.slice(0, TABLE_MAX_COLS);
      });
      setSelection({ start: { row: anchor.row, col: insertAt }, end: { row: anchor.row, col: insertAt } });
      setEditingCell(null);
    },
    [selection],
  );

  const deleteColumn = React.useCallback(() => {
    if (getColumnCount(table) <= 1) return;
    const anchor = getSelectedAnchor(selection);
    const nextCol = Math.max(0, anchor.col - 1);
    setTable((current) => current.map((row) => row.filter((_, colIndex) => colIndex !== anchor.col)));
    setColumnWidths((current) => current.filter((_, colIndex) => colIndex !== anchor.col));
    setSelection({ start: { row: anchor.row, col: nextCol }, end: { row: anchor.row, col: nextCol } });
    setEditingCell(null);
  }, [selection, table]);

  const addRow = React.useCallback((side: "before" | "after") => {
    const anchor = getSelectedAnchor(selection);
    const insertAt = side === "before" ? anchor.row : anchor.row + 1;
    const columnCount = getColumnCount(table);
    const nextRow = Array.from({ length: columnCount }, (_, col) =>
      createCell(insertAt, col),
    );
    setTable((current) => {
      const next = cloneTable(current);
      next.splice(insertAt, 0, nextRow);
      return next.slice(0, TABLE_MAX_ROWS);
    });
    setSelection({ start: { row: insertAt, col: Math.min(anchor.col, columnCount - 1) }, end: { row: insertAt, col: Math.min(anchor.col, columnCount - 1) } });
    setEditingCell(null);
  }, [selection, table]);

  const deleteRow = React.useCallback(() => {
    if (table.length <= 1) return;
    const anchor = getSelectedAnchor(selection);
    const nextRow = Math.max(0, anchor.row - 1);
    setTable((current) => current.filter((_, rowIndex) => rowIndex !== anchor.row));
    setSelection({ start: { row: nextRow, col: anchor.col }, end: { row: nextRow, col: anchor.col } });
    setEditingCell(null);
  }, [selection, table.length]);

  const mergeCells = React.useCallback(() => {
    if (!selectedRange) return;
    const { fromRow, toRow, fromCol, toCol } = selectedRange;
    if (fromRow === toRow && fromCol === toCol) return;
    setTable((current) => {
      const next = cloneTable(current);
      const anchor = next[fromRow]?.[fromCol];
      if (!anchor) return current;
      const mergedText: string[] = [];
      for (let row = fromRow; row <= toRow; row += 1) {
        for (let col = fromCol; col <= toCol; col += 1) {
          const cell = next[row]?.[col];
          if (!cell || cell.hidden) continue;
          if (cell.text.trim()) mergedText.push(cell.text.trim());
          if (row !== fromRow || col !== fromCol) cell.hidden = true;
        }
      }
      anchor.hidden = false;
      anchor.rowspan = toRow - fromRow + 1;
      anchor.colspan = toCol - fromCol + 1;
      anchor.text = mergedText.join(" / ");
      return next;
    });
    setSelection({ start: { row: fromRow, col: fromCol }, end: { row: fromRow, col: fromCol } });
    setEditingCell(null);
  }, [selectedRange]);

  const splitCell = React.useCallback(() => {
    const anchor = getSelectedAnchor(selection);
    setTable((current) => {
      const next = cloneTable(current);
      const cell = next[anchor.row]?.[anchor.col];
      if (!cell || cell.hidden) return current;
      const rowSpan = cell.rowspan;
      const colSpan = cell.colspan;
      cell.rowspan = 1;
      cell.colspan = 1;
      for (let row = anchor.row; row < anchor.row + rowSpan; row += 1) {
        for (let col = anchor.col; col < anchor.col + colSpan; col += 1) {
          if (row === anchor.row && col === anchor.col) continue;
          const covered = next[row]?.[col];
          if (covered) {
            covered.hidden = false;
            covered.rowspan = 1;
            covered.colspan = 1;
          }
        }
      }
      return next;
    });
    setEditingCell(null);
  }, [selection]);

  const mergeOrSplit = React.useCallback(() => {
    const anchor = getSelectedAnchor(selection);
    const cell = table[anchor.row]?.[anchor.col];
    if (cell && (cell.rowspan > 1 || cell.colspan > 1)) {
      splitCell();
      return;
    }
    mergeCells();
  }, [mergeCells, selection, splitCell, table]);

  const shouldSplitSelection = Boolean(
    selectedCell && (selectedCell.rowspan > 1 || selectedCell.colspan > 1),
  );
  const canMergeSelection = Boolean(
    selectedRange &&
    (selectedRange.fromRow !== selectedRange.toRow ||
      selectedRange.fromCol !== selectedRange.toCol),
  );
  const canMergeOrSplitSelection = shouldSplitSelection || canMergeSelection;

  const toggleHeaderRow = React.useCallback(() => {
    if (!selectedRange) return;
    setTable((current) => {
      const next = cloneTable(current);
      for (let row = selectedRange.fromRow; row <= selectedRange.toRow; row += 1) {
        next[row] = next[row].map((cell) => ({
          ...cell,
          type: cell.type === "header" ? "cell" : "header",
        }));
      }
      return next;
    });
  }, [selectedRange]);

  const toggleHeaderColumn = React.useCallback(() => {
    if (!selectedRange) return;
    setTable((current) => {
      const next = cloneTable(current);
      for (const row of next) {
        for (let col = selectedRange.fromCol; col <= selectedRange.toCol; col += 1) {
          if (row[col]) row[col].type = row[col].type === "header" ? "cell" : "header";
        }
      }
      return next;
    });
  }, [selectedRange]);

  const toggleHeaderCell = React.useCallback(() => {
    const anchor = getSelectedAnchor(selection);
    setTable((current) => {
      const next = cloneTable(current);
      const cell = next[anchor.row]?.[anchor.col];
      if (cell) cell.type = cell.type === "header" ? "cell" : "header";
      return next;
    });
  }, [selection]);

  const setFixedRow = React.useCallback(() => {
    const anchor = getSelectedAnchor(selection);
    onChange((prev) => ({
      ...prev,
      fixedRow: prev.fixedRow === anchor.row ? undefined : anchor.row,
    }));
  }, [onChange, selection]);

  const setFixedCol = React.useCallback(() => {
    const anchor = getSelectedAnchor(selection);
    onChange((prev) => ({
      ...prev,
      fixedCol: prev.fixedCol === anchor.col ? undefined : anchor.col,
    }));
  }, [onChange, selection]);

  const handleImportXlsx = React.useCallback(
    async (file: File) => {
      try {
        const imported = await importXlsxFileToTable(file);
        setTable(imported.table);
        setColumnWidths(imported.columnWidths);
        setSelection({ start: { row: 0, col: 0 }, end: { row: 0, col: 0 } });
        setEditingCell(null);
        setHoveredResizeCol(null);
        setResizing(null);
        setError(null);
        onChange((prev) => ({
          ...prev,
          fixedRow: undefined,
          fixedCol: undefined,
        }));
      } catch (err: any) {
        setError(
          err?.message ??
          "Unable to import this Excel file.",
        );
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [onChange],
  );

  const startColumnResize = React.useCallback(
    (event: React.PointerEvent, colIndex: number) => {
      event.preventDefault();
      event.stopPropagation();
      setEditingCell(null);
      const startX = event.clientX;
      const startWidth = columnWidths[colIndex] ?? DEFAULT_COLUMN_WIDTH;
      setResizing({ colIndex, startX, startWidth });
      setHoveredResizeCol(colIndex);
      const handlePointerMove = (moveEvent: PointerEvent) => {
        const delta = moveEvent.clientX - startX;
        setColumnWidths((current) =>
          resizeColumnWidths(current, colIndex, startWidth + delta),
        );
      };
      const handlePointerUp = () => {
        setResizing(null);
        setHoveredResizeCol(null);
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [columnWidths],
  );

  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
  const getColumnStickyOffset = React.useCallback(
    (colIndex: number) =>
      columnWidths
        .slice(0, colIndex)
        .reduce((sum, width) => sum + width, 44),
    [columnWidths],
  );
  const getRowStickyOffset = React.useCallback(
    (rowIndex: number) =>
      rowHeights
        .slice(0, rowIndex)
        .reduce((sum, height) => sum + height, 32),
    [rowHeights],
  );

  return (
    <CustomModal
      open={open}
      onOpenChange={onOpenChange}
      maxWidth="max-w-5xl"
      bodyClassName="p-5 space-y-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Table editor</h2>
          <p className="text-sm text-gray-600">
            Click to select, shift-click for a range, double-click to edit.
          </p>
          {stats ? (
            <p className="text-xs text-gray-500">
              {stats.rows} rows · {stats.cols} columns
              {fixedRow ? ` · fixed rows 1-${fixedRow}` : ""}
              {fixedCol ? ` · fixed columns 1-${fixedCol}` : ""}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          data-safe-nav="true"
          className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          onClick={() => onOpenChange(false)}
        >
          Done
        </button>
      </div>

      {error ? (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {error}
        </div>
      ) : null}
      {clamped ? (
        <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
          Large table data was truncated to stay under {TABLE_MAX_ROWS} x{" "}
          {TABLE_MAX_COLS}.
        </div>
      ) : null}

      <TableEditorToolbar
        canMergeOrSplitSelection={canMergeOrSplitSelection}
        columnWidths={columnWidths}
        fixedCol={fixedCol}
        fixedRow={fixedRow}
        hasSelection={hasSelection}
        onAddColumn={addColumn}
        onAddRow={addRow}
        onDeleteColumn={deleteColumn}
        onDeleteRow={deleteRow}
        onExportExcel={() => {
          exportTableToXlsx(table, columnWidths).catch((err: any) => {
            setError(err?.message ?? "Unable to export this table.");
          });
        }}
        onFixColumn={setFixedCol}
        onFixRow={setFixedRow}
        onImportExcel={() => fileInputRef.current?.click()}
        onMergeOrSplit={mergeOrSplit}
        onSetAlign={setSelectedAlign}
        onSetColumnWidths={setColumnWidths}
        onSetDirection={setSelectedDirection}
        onToggleBold={toggleSelectedBold}
        onToggleHeaderCell={toggleHeaderCell}
        onToggleHeaderColumn={toggleHeaderColumn}
        onToggleHeaderRow={toggleHeaderRow}
        onToggleItalic={toggleSelectedItalic}
        selectedAnchor={selectedAnchor}
        selectedCell={selectedCell}
        shouldSplitSelection={shouldSplitSelection}
        table={table}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleImportXlsx(file);
        }}
      />

      <label className="block space-y-1 text-sm text-gray-700">
        <span className="block font-medium">Selected cell text</span>
        <input
          value={selectedCell?.text ?? ""}
          onChange={(event) => updateCellText(event.target.value)}
          className="w-full rounded border border-gray-300 px-3 py-2"
          placeholder="Edit selected cell text"
        />
      </label>

      <TableGrid
        columnWidths={columnWidths}
        editingCell={editingCell}
        fixedCol={fixedCol}
        fixedRow={fixedRow}
        getColumnStickyOffset={getColumnStickyOffset}
        getRowStickyOffset={getRowStickyOffset}
        hoveredResizeCol={hoveredResizeCol}
        onCellTextChange={updateCellText}
        onSelectCell={selectCell}
        onSetEditingCell={setEditingCell}
        onSetHoveredResizeCol={setHoveredResizeCol}
        onSetSelection={setSelection}
        onStartColumnResize={startColumnResize}
        resizing={resizing}
        rowHeights={rowHeights}
        rowRefs={rowRefs}
        selection={selection}
        table={table}
        totalWidth={totalWidth}
      />

      <div className="grid gap-3 md:grid-cols-2">
        <label className="space-y-1 text-sm text-gray-700">
          <span className="block font-medium">Caption (optional)</span>
          <input
            type="text"
            value={data?.caption ?? ""}
            onChange={(event) =>
              onChange((prev) => ({ ...prev, caption: event.target.value }))
            }
            className="w-full rounded border border-gray-300 px-3 py-2"
            placeholder="Add a short caption"
          />
        </label>
        <label className="space-y-1 text-sm text-gray-700">
          <span className="block font-medium">Variant</span>
          <select
            className="w-full rounded border border-gray-300 px-3 py-2"
            value={data?.variant ?? "default"}
            onChange={(event) =>
              onChange((prev) => ({
                ...prev,
                variant: event.target.value === "compact" ? "compact" : "default",
              }))
            }
          >
            <option value="default">Default</option>
            <option value="compact">Compact</option>
          </select>
        </label>
      </div>
    </CustomModal>
  );
}
